export enum IntroductionState {
  Language = 'language',
  Course = 'course',
  PersonalProfile = 'personalProfile',
  FreeCredit = 'freeCredit',
  ReserveProva = 'reserveProva',
  ReserveSkipped = 'reserveSkipped',
  FutureLesson = 'futureLesson',
  Cspa = 'cspa',
  Welcome = 'welcome',
  Finished = 'finished',
  InvitedToColNew = 'invited',
  WelcomedOnColNew = 'welcomedOnColNew',
}

export enum LessonType {
  Standard = 'Standard',
  Revision = 'Revision',
  QuickStageRevision = 'QuickStageRevision',
  Introduction = 'Introduction',
  Prova = 'Prova',
  Exam = 'Exam',
  Test = 'Test',
  Conversation = 'Conversation'
}

/**
 * Lesson instance kind — distinguishes the Callan online lesson (col) from a
 * generic lesson. This is the lesson's underlying instance class, NOT its
 * pedagogical LessonType (Standard, Revision, Prova, ...).
 */
export enum LessonCategory {
  Col = 'col',
  Generic = 'generic'
}

export type JsonObject = Record<string, unknown>;

export enum LessonStatus {
  Initializing = 'Initializing',
  Booked = 'Booked',
  Due = 'Due',
  InProgress = 'InProgress',
  Complete = 'Complete',
  Cancel = 'Cancel',
  InvalidatedByTeacher = 'InvalidatedByTeacher',
  InvalidatedByStudent = 'InvalidatedByStudent'
}

export interface LessonRef {
  id: number;
  eid: string;
}

export interface SchoolPersonRef {
  id: number;
}

export interface PersonRef {
  id: number;
}

export interface PersonDetails {
  name: string;
  surname: string;
  emailAddress: string;
}

export interface PersonAccountRef {
  accountId: number;
}

export enum PersonStatus {
  Active = 'Active',
  Inactive = 'Inactive',
  Locked = 'Locked',
  Deleted = 'Deleted'
}

export interface PersonDetailedRef extends PersonRef, PersonDetails, PersonAccountRef {}

export interface SchoolRef {
  id: number;
}

export interface SchoolMemberRef extends SchoolPersonRef {}

export interface SchoolMemberDetailsBase<P extends PersonRef> {
  person: P;
}

export interface SchoolMemberPersonDetailed extends SchoolMemberDetailsBase<PersonDetailedRef> {}

export interface StudentRef extends SchoolMemberRef {
  id: number;
}

export interface Student extends StudentRef, SchoolMemberPersonDetailed {
  status: PersonStatus;
}

export interface V3SchoolStudent extends Student {
  school: SchoolRef;
}

export interface TeacherRef extends SchoolMemberRef {
  id: number;
}

export interface Teacher extends TeacherRef, SchoolMemberPersonDetailed {
  status: PersonStatus;
}

export interface V3SchoolTeacher extends Teacher {
  school: SchoolRef;
}

export interface GroupRef {
  id: number;
}

export interface GroupDetails {
  name: string;
}

export enum GroupStatus {
  Activating = 'Activating',
  Active = 'Active',
  Locked = 'Locked',
  Deleted = 'Deleted'
}

export interface Group extends GroupRef, GroupDetails {
  status: GroupStatus;
}

export interface GroupMember {
  id: number;
  member: SchoolMemberRef;
}

export interface GroupMemberBase<SM extends SchoolMemberRef> extends GroupMember {
  member: SM;
}

export interface ProductRef {
  code: string;
}

export interface V3Product extends ProductRef {
  name: string;
}

export interface PriceListRef {
  name: string;
}

export interface VariantPrice {
  value: number;
  currency: string;
  priceList?: PriceListRef | null;
}

export interface ProductPreview extends ProductRef {
  name?: string | null;
  description?: string | null;
  labels: string[];
}

export interface ProductVariantPreview {
  code: string;
  product: ProductPreview;
  name?: string | null;
  description?: string | null;
  labels: string[];
  price?: VariantPrice | null;
}

export interface CourseRef {
  code: string;
}

export interface Course extends CourseRef {
  name: string;
}

export interface CompetenceRef {
  code: string;
}

export interface Competence extends CompetenceRef {
  name: string;
}

export interface CompetenceMapping {
  competenceCode: string | null;
  courseCode: string | null;
}

export interface CourseTeacherProfileDetailsBase<T extends CompetenceRef> {
  competences: T[];
  description: string;
  accent: string;
  videoUrl: string;
}

export interface V3CourseTeacherProfileDetails
  extends CourseTeacherProfileDetailsBase<CompetenceRef> {}

export interface V3CourseTeacherProfile extends V3CourseTeacherProfileDetails {
  course: CourseRef;
}

export interface LessonParticipantBase {
  eid: string;
}

export interface LessonTeacherParticipant<T extends TeacherRef> extends LessonParticipantBase {
  reference: T;
}

export interface LessonStudentParticipant<S extends StudentRef> extends LessonParticipantBase {
  reference: S;
}

export interface ParticipantProgressDetailsBase {}

export interface AttendanceDetails extends ParticipantProgressDetailsBase {
  attended: boolean;
}

export interface V3LessonParticipantProgressDetails extends AttendanceDetails {}

export interface LessonTeacherWithProgress<T extends TeacherRef, P extends ParticipantProgressDetailsBase>
  extends LessonTeacherParticipant<T> {
  progress: P;
}

export interface LessonStudentWithProgress<S extends StudentRef, P extends ParticipantProgressDetailsBase> {
  student: LessonStudentParticipant<S>;
  progress: P;
}

export interface LessonGroupParticipant extends LessonParticipantBase {}

export interface V3LessonReservationTimeDetails {
  plannedStartDate: Date;
  plannedDuration: number;
  timezone: string;
}

export interface LessonStats {
  started: Date;
  duration: number;
}

export interface V3LessonMetric extends V3LessonReservationTimeDetails, LessonStats {}

export interface LessonBase<LM extends V3LessonReservationTimeDetails> extends LessonRef {
  status: LessonStatus;
  type: LessonType;
  createDate: Date;
  lessonMetric: LM;
}

export interface ProgressDetailsBase {}

export interface CallanProgressDetails extends ProgressDetailsBase {
  start: number | null;
  reading: number | null;
  nwp: number | null;
  headwords: string | null;
  productVersion: string | null;
}

export interface GenericProgressDetails extends CallanProgressDetails {
  stage: string | null;
  product: string | null;
  paragraph: string | null;
  dictation: string | null;
  readingLesson: string | null;
  comment: string | null;
}

export interface ApiLessonCancellationDetails {
  cancellationDate: Date;
  cancelledByTeacher: boolean;
  cancellationReason: string;
}

export enum LessonFlagType {
  PoorConnection = 'PoorConnection',
  ClassroomIssue = 'ClassroomIssue',
  StudentNeedsHelp = 'StudentNeedsHelp',
  Other = 'Other',
  StudentNotShow = 'StudentNotShow',
  TeacherNotShow = 'TeacherNotShow'
}

export enum LessonFlagStatus {
  Submitted = 'Submitted',
  Solved = 'Solved'
}

export enum LessonFlagTargetType {
  Lesson = 'Lesson',
  Teacher = 'Teacher',
  Student = 'Student'
}

export interface ApiLessonFlag {
  id: number;
  flagType: LessonFlagType;
  description: string;
  date: Date;
  solveDate: Date | null;
  status: LessonFlagStatus;
  target: LessonFlagTargetType;
}

export interface ApiLessonMessage {
  lessonComment: string;
  privateLessonComment: string;
  message: string;
}

export interface LessonMessageDto extends ApiLessonMessage {}

export interface LFCancellationDetails {
  cancellationDetails: ApiLessonCancellationDetails;
}

export interface LFWithCourse<C extends CourseRef, CT extends CompetenceRef> {
  course: C;
  competence: CT;
  productVersion: string;
}

export interface LFWithProgress {
  progressCommited: ProgressDetailsBase;
}

export interface LFWithFlags {
  flags: ApiLessonFlag[];
}

export interface LFWithMessage {
  message: ApiLessonMessage;
}

export interface LFWithRoom {
  roomUrl: string;
}

export interface LFWithStudentMessage {
  studentMessage: string;
}

export interface LFWithTeacher<T extends TeacherRef> {
  teacher: LessonTeacherParticipant<T>;
}

export interface LFWithTeacherProgress<T extends TeacherRef> extends LFWithTeacher<T> {
  teacher: LessonTeacherWithProgress<T, ParticipantProgressDetailsBase>;
}

export interface LFWithStudents<S extends StudentRef> {
  students: LessonStudentParticipant<S>[];
}

export interface LFWithStudentProgress<S extends StudentRef> extends LFWithStudents<S> {
  studentsWithProgress: LessonStudentWithProgress<S, ParticipantProgressDetailsBase>[];
}

export interface LFWithGroup {
  group: LessonGroupParticipant;
}

export interface LFPreviewBase<LM extends V3LessonReservationTimeDetails, C extends CourseRef, CT extends CompetenceRef>
  extends LessonBase<LM>, LFWithCourse<C, CT>, LFCancellationDetails, LFWithProgress, LFWithRoom {}

export interface LFViewBase {}

export interface LFTeacherPreview
  extends LFViewBase,
    LFPreviewBase<V3LessonMetric, Course, CompetenceRef>,
    LFWithStudents<Student>,
    LFWithFlags {}

export interface LFTeacherGeneral extends LFViewBase, LFTeacherPreview {}

export interface LFTeacherGeneralWithMessage
  extends LFViewBase,
    LFTeacherGeneral,
    LFWithMessage,
    LFWithStudentMessage {}

export interface LFTeacherFull extends LFViewBase, LFTeacherGeneralWithMessage, LFWithTeacher<Teacher> {}

export interface LFStudentPreview
  extends LFViewBase,
    LFPreviewBase<V3LessonMetric, Course, CompetenceRef>,
    LFWithTeacher<Teacher> {}

export interface LFStudentGeneral
  extends LFViewBase,
    LFStudentPreview,
    LFWithStudents<Student>,
    LFWithMessage,
    LFWithFlags {}

export interface LFPreview extends LFViewBase, LessonBase<V3LessonMetric>, LFWithCourse<Course, CompetenceRef>, LFWithFlags {}

export interface LFPreviewWithFullProgress
  extends LFPreview,
    LFWithProgress,
    LFWithTeacherProgress<TeacherRef>,
    LFWithStudentProgress<StudentRef> {}

export interface LFGeneric
  extends LFViewBase,
    LessonBase<V3LessonMetric>,
    LFWithCourse<CourseRef, CompetenceRef>,
    LFWithGroup,
    LFWithTeacher<TeacherRef>,
    LFWithStudents<StudentRef> {}

export type LessonView =
  | LFTeacherPreview
  | LFTeacherGeneral
  | LFTeacherGeneralWithMessage
  | LFTeacherFull
  | LFStudentPreview
  | LFStudentGeneral
  | LFPreview
  | LFPreviewWithFullProgress
  | LFGeneric;

export type LessonProgressDetails = CallanProgressDetails | GenericProgressDetails;

export interface CancellationRequest {
  reason: string;
}

export interface InvalidationRequest extends CancellationRequest {
  flags: LessonFlagType[];
}

export interface LessonBundleRef {
  id: number;
}

export interface LessonBundleDetailsBase<P extends ProductRef> {
  product: P;
  date: Date;
  available: number;
  expiryDate: Date | null;
  registeredLesson: number;
  name: string;
  price: string;
  bundleDuration: number | null;
  note: string;
}

export interface LessonBundleDetails extends LessonBundleDetailsBase<ProductRef> {}

export interface LessonBundleBase<P extends ProductRef> extends LessonBundleRef, LessonBundleDetailsBase<P> {}

export interface LessonBundle extends LessonBundleRef, LessonBundleDetails {}

export enum LessonCommitStatus {
  PENDING = 'PENDING',
  CANCELLED = 'CANCELLED',
  CONFIRMED = 'CONFIRMED'
}

export interface LessonCommitRef {
  id: number;
}

export interface LessonCommitBase<L extends LessonRef, B extends LessonBundleRef> extends LessonCommitRef {
  lesson: L;
  bundle: B;
  status: LessonCommitStatus;
  createDate: Date;
  updateDate: Date;
}

export interface LessonCommit extends LessonCommitBase<LessonRef, LessonBundleRef> {}

export interface ColProvaContextDetails {
  startExpiryDate: Date | null;
  starterBuyDate: Date | null;
}

export interface CourseContextBase {}

export interface V3CourseContextBase extends CourseContextBase {
  current: boolean;
}

export interface CourseContextDetailsBase<L extends LessonRef> extends CourseContextBase {
  lastRegularLesson: L | null;
  lastExam: L | null;
  lastRevision: L | null;
  lastQuickStageRevision: L | null;
  firstLesson: L | null;
  lessons: number;
  exams: number;
  revisions: number;
  quickStageRevisions: number;
  overall: number;
  revisionProgress: number;
  lessonsProgress: number;
  quickStageRevisionProgress: number;
  estimatedUntilFinish: number;
}

export interface ColCourseContext extends CourseContextDetailsBase<LessonRef>, V3CourseContextBase {
  course?: CourseRef | null;
}

export interface ProductContextBase {}

export interface ProductContextDetails extends ProductContextBase {
  productCode?: string | null;
  nextLessonType: LessonType;
  productVersion: string | null;
}

export interface PersonalSettings {
  lang: string;
  phone: string;
  skype: string;
  notifBookingConfirm: boolean;
  notifOffers: boolean;
  allowBookingTeacherChange: boolean;
  introductionState: string;
}

export enum LessonStatusAction {
  Cancel = 'cancel',
  Invalidate = 'invalidate',
}

export interface LessonCancellationRequestDto {
  reason: string;
}

export interface LessonInvalidationRequestDto {
  reason: string;
  flags?: LessonFlagType[];
}

export interface LessonStatusRequestDto {
  reason: string;
  flags?: LessonFlagType[];
}

export interface LessonFlagDto {
  id?: number;
  flagType?: LessonFlagType;
  description?: string;
  date?: Date;
  solveDate?: Date | null;
  status?: LessonFlagStatus;
  target?: LessonFlagTargetType;
}

export enum LessonProgressType {
  COL = 'COL',
  GENERIC = 'GENERIC'
}

export interface LessonCallanProgressDetailsDto extends CallanProgressDetails {
  '@type': 'callan';
}

export interface LessonGenericProgressDetailsDto extends GenericProgressDetails {
  '@type': 'generic';
}

export type LessonProgressDetailsDto = LessonCallanProgressDetailsDto | LessonGenericProgressDetailsDto;
